import React, { useState } from 'react';
import { DeviationChart } from '@scope/scope-one-ui/core/Charts/DeviationChart/DeviationChart';
import { Box, Card, ChartLegend, Divider, Select, Typography } from '@scope/scope-one-ui/core';
import { useTranslation } from 'react-i18next';
import { CHARTS_COLORS, COLORS } from '@scope/scope-one-ui/theme';
import { useParams } from 'react-router-dom';
import format from 'date-fns/format';
import { ChartOption } from './ESGScoresOverTime.d';
import { useGetE_S_GData, useGetESGHistory, useGetESGTotalData } from '../../services/esgSentiment';
import { StyledChipButton } from './ESGScoresOverTime.styled';
import { EsgSentimentParams, ScoresOverTimeData } from '../../types/esgSentiment';
import { ChartLoader } from './ESGScoresOverTime.loader';
import { PALETTE } from '@scope/scope-one-ui/theme/palette';

const timeRangeSelect = [
  { value: '7days', label: '7 days' },
  { value: '1months', label: '1 month' },
  { value: '3months', label: '3 months' },
  { value: '6months', label: '6 months' },
  { value: '1year', label: '1 year' },
  { value: 'max', label: 'Max' },
];

const chartOptions: ChartOption[] = [
  { label: 'ESG TOTAL', value: 'esg' },
  { label: 'ENVIRONMENT', value: 'e' },
  { label: 'SOCIAL', value: 's' },
  { label: 'GOVERNANCE', value: 'g' },
];

const [defaultOption] = chartOptions;

const [, defaultRange] = timeRangeSelect;

export const ESGScoresOverTime = () => {
  const { t } = useTranslation();

  const [selectedChartOption, setSelectedChartOption] = useState<ChartOption>(defaultOption);
  const [timeRange, setTimeRange] = useState<string>(defaultRange.value);
  const { entityId } = useParams<EsgSentimentParams>();

  const { data: esgTotalLine, loading: esgTotalLineLoading } = useGetESGTotalData(
    entityId!,
    timeRange,
  );
  const { data: ESGData } = useGetE_S_GData(entityId!, timeRange);
  const { data: deviationData } = useGetESGHistory(selectedChartOption.value, entityId!, timeRange);

  const chartData = () => {
    const { label, value } = selectedChartOption;

    return {
      data: mapChartData(
        value === 'esg'
          ? esgTotalLine?.esgMainLine
          : ESGData?.[{ e: 'eCompanyScores', s: 'sCompanyScores', g: 'gCompanyScores' }[value]],
      ),
      legendItems: [
        { label, color: CHARTS_COLORS[0] },
        { label: '1 Standard Deviation within peer group', color: PALETTE.blue5 },
        { label: '2 Standard Deviations within peer group', color: PALETTE.blue6 },
      ],
    };
  };

  const changeTimeRange = (value) => setTimeRange(value);

  const mapChartData = (data: ScoresOverTimeData[] = []) =>
    data
      .reverse()
      .map(({ value, date }) => ({ x: format(new Date(date), 'dd MMMM yyyy'), y: value }));

  return (
    <Card>
      <Box display="flex" justifyContent="space-between" mx={2} my={1} alignItems="center">
        <Box display="flex" alignItems="baseline" gridGap={1}>
          <Typography variant="h1">{t('Scores over time')}</Typography>
          <Typography variant="h3" color={COLORS.typography.main}>
            {t('Sentiment Comparison')}
          </Typography>
        </Box>
        <Select
          options={timeRangeSelect}
          placeholder={defaultRange.label}
          onChange={changeTimeRange}
        />
      </Box>
      <Divider />
      {esgTotalLineLoading ? (
        <ChartLoader />
      ) : (
        <>
          <Box display="flex" gridGap={2} m={2}>
            {chartOptions.map((option) => (
              <StyledChipButton
                key={option.value}
                size="medium"
                pressed={selectedChartOption === option}
                onClick={() => setSelectedChartOption(option)}
              >
                {option.label}
              </StyledChipButton>
            ))}
          </Box>
          <Box mr={2} pb={2}>
            <DeviationChart
              lineData={[
                {
                  name: selectedChartOption.label,
                  data: chartData().data,
                },
              ]}
              deviationData={
                deviationData && {
                  name: selectedChartOption.label,
                  data: deviationData.results,
                }
              }
              dots="none"
              yAxisRange={[0, 10]}
            />
          </Box>
          <Box display="flex" justifyContent="center" mb={4}>
            <ChartLegend items={chartData().legendItems} size="medium" />
          </Box>
        </>
      )}
    </Card>
  );
};
